// ENG-2277 (HTML Forms): a table toolbar control for which of a table's lines print. The choice is
// stored as the table's data-lines, which both PDF table renderers read (form-print.utils.ts).
import type { Editor } from "tinymce";

import {
  TABLE_LINES_ATTRIBUTE,
  TableLinesMode,
  readTableLinesMode
} from "components/pdf-generation-v3/form-print.utils";

export const TABLE_LINES_BUTTON = "formtablelines";

export const TABLE_LINES_MENU: { mode: TableLinesMode; text: string }[] = [
  { mode: "all", text: "All lines" },
  { mode: "outside", text: "Outside border only" },
  { mode: "rows", text: "Lines between rows" },
  { mode: "none", text: "No lines" }
];

export const setTableLines = (
  table: HTMLTableElement,
  mode: TableLinesMode
) => {
  table.setAttribute(TABLE_LINES_ATTRIBUTE, mode);
  // TinyMCE's content CSS draws a `border` table's cells solid, whatever data-lines says, and the
  // PDF never reads the attribute.
  table.removeAttribute("border");
};

const tableAtCursor = (editor: Editor): HTMLTableElement | null =>
  editor.dom.getParent(
    editor.selection.getNode(),
    "table"
  ) as HTMLTableElement | null;

export const addTableLinesButton = (editor: Editor) => {
  editor.ui.registry.addMenuButton(TABLE_LINES_BUTTON, {
    text: "Lines",
    tooltip: "Table lines (what prints)",
    fetch: (callback) =>
      callback(
        TABLE_LINES_MENU.map(({ mode, text }) => ({
          type: "togglemenuitem",
          text,
          onSetup: (api) => {
            const table = tableAtCursor(editor);
            api.setActive(
              Boolean(table) &&
                readTableLinesMode(
                  table?.getAttribute(TABLE_LINES_ATTRIBUTE)
                ) === mode
            );
            return () => undefined;
          },
          onAction: () => {
            const table = tableAtCursor(editor);
            if (!table) return;
            editor.undoManager.transact(() => setTableLines(table, mode));
            editor.nodeChanged();
          }
        }))
      )
  });
};

/** The table toolbar with the lines control first, when the field offers it. */
export const withTableLinesButton = (tableToolbar: string, enabled: boolean) =>
  enabled && tableToolbar
    ? `${TABLE_LINES_BUTTON} | ${tableToolbar}`
    : tableToolbar;
